/* CUSTOM — Add to bag for a component bundle.

   A Shopify component bundle cannot be sold on a subscription, so products
   flagged custom.add_components post their bundle contents as separate line
   items instead — see snippets/custom-bundle-buy-buttons.liquid for the whole
   story.

   This lives in its own file because both the product page's buy box and the
   quick add on a product card render that form, and they appear on completely
   different templates. Anything rendering a `form[data-mg-bundle]` must load
   this alongside it.

   The form is real and submits without JavaScript. This adds the selling plan
   (per line, because a top-level one is ignored beside an items[] payload), the
   quantity, and the cart drawer. */

(() => {
  'use strict';

  document.querySelectorAll('form[data-mg-bundle]').forEach((bundleForm) => {

    /* Several sections can pull this file in, and a browser runs every
       <script src> it is given — identical URLs are not deduplicated. The flag is
       per form rather than global, so a page with a product's buy box and a grid
       of cards binds each of them exactly once. */
    if (bundleForm.dataset.mgBundleBound === 'true') return;
    bundleForm.dataset.mgBundleBound = 'true';

    /* Dawn's cart notification keys its product markup off one line item —
       `renderContents` reads `parsedState.key` — and a multi-item add returns no
       such key, so it looked for `cart-notification-product-undefined`, found
       nothing and threw on innerHTML. That is why adding the Care Duo or a whole
       routine never popped the notification.

       The section itself already renders every line in the cart, each under its
       own id, so the data was always there. Hand Dawn the first key to run its
       own plumbing, then widen the markup to every line that was added. */
    const showCartNotification = (cart, body) => {
      const added = Array.isArray(body.items) ? body.items : [body];
      cart.renderContents({ ...body, key: body.key ?? added[0]?.key });

      if (added.length < 2) return;
      const source = body.sections?.['cart-notification-product'];
      const target = document.getElementById('cart-notification-product');
      if (!source || !target) return;

      const rendered = new DOMParser().parseFromString(source, 'text/html');
      target.innerHTML = added
        .map((item) => rendered.querySelector(`[id="cart-notification-product-${item.key}"]`)?.outerHTML ?? '')
        .join('');
    };

    /* Re-render just the cart bubble from the server, for when the notification
       can't render itself. Cheap, and it keeps the header honest. */
    const refreshCartCount = async () => {
      try {
        const response = await fetch(`${window.location.pathname}?section_id=cart-icon-bubble`);
        const markup = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = markup.querySelector('.shopify-section');
        const current = document.querySelector('#cart-icon-bubble');
        if (fresh && current) current.innerHTML = fresh.innerHTML;
      } catch (error) {
        console.warn('Could not refresh the cart count.', error);
      }
    };

    const lineQuantities = bundleForm.querySelectorAll('[data-mg-bundle-quantity]');
    const linePlans = bundleForm.querySelectorAll('[data-mg-bundle-plan]');
    const errorEl = bundleForm.querySelector('[data-mg-bundle-error]');
    const submit = bundleForm.querySelector('[type="submit"]');

    /* The selector renders one hidden input associated to this form by id, and
       disables it for a one-time purchase — so "disabled" is the signal, not an
       empty value. */
    const selectedPlan = () => {
      const input = document.querySelector(`input[name="selling_plan"][form="${bundleForm.id}"]`);
      return input && !input.disabled && input.value ? input.value : null;
    };

    const quantity = () => {
      const input = document.querySelector(`[name="quantity"][form="${bundleForm.id}"]`);
      const parsed = Number(input?.value);
      return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 1;
    };

    const syncLines = () => {
      const plan = selectedPlan();
      const qty = quantity();
      lineQuantities.forEach((input) => {
        input.value = String(qty);
      });
      linePlans.forEach((input) => {
        input.disabled = plan === null;
        input.value = plan ?? '';
      });
    };

    // Keep the hidden lines in step as the customer changes their mind, so the
    // form is always correct even if it ends up submitting natively.
    document.addEventListener('change', syncLines);
    syncLines();

    bundleForm.addEventListener('submit', async (event) => {
      if (typeof fetchConfig !== 'function' || typeof routes === 'undefined') return;

      event.preventDefault();
      syncLines();
      if (errorEl) errorEl.hidden = true;

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(bundleForm);
      if (cart) {
        formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        cart.setActiveElement(document.activeElement);
      }
      config.body = formData;

      submit?.classList.add('loading');
      submit?.setAttribute('aria-disabled', 'true');
      submit?.querySelector('.loading__spinner')?.classList.remove('hidden');

      try {
        const response = await fetch(routes.cart_add_url, config);
        const body = await response.json();

        if (body.status) {
          if (errorEl) {
            errorEl.textContent = body.description || body.message;
            errorEl.hidden = false;
          }
          return;
        }

        if (!cart) {
          window.location = routes.cart_url;
          return;
        }

        cart.classList.remove('is-empty');

        try {
          showCartNotification(cart, body);
        } catch (renderError) {
          /* The add itself has already succeeded, so a notification that won't
             render must never be reported as a failed add. Keep the count honest
             and say nothing. */
          console.warn('Cart notification could not render.', renderError);
          await refreshCartCount();
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mg-bundle-form', cartData: body });
        }
      } catch (error) {
        console.error(error);
        if (errorEl) {
          errorEl.textContent = 'Something went wrong adding that. Please try again.';
          errorEl.hidden = false;
        }
      } finally {
        submit?.classList.remove('loading');
        submit?.removeAttribute('aria-disabled');
        submit?.querySelector('.loading__spinner')?.classList.add('hidden');
      }
  });
  });
})();
